/**
 * LoadingSpinner - Enhetlig laddningsindikator
 * Används för att visa laddningsstatus i hela appen
 */

import React from 'react';

export type LoadingSpinnerSize = 'xs' | 'sm' | 'md' | 'lg';
export type LoadingSpinnerVariant = 'primary' | 'default' | 'muted' | 'white';

export interface LoadingSpinnerProps {
  /** Storlek: xs (4), sm (5), md (8), lg (12) */
  size?: LoadingSpinnerSize;
  /** Färgvariant */
  variant?: LoadingSpinnerVariant;
  /** Ytterligare CSS-klasser */
  className?: string;
}

const SIZE_MAP: Record<LoadingSpinnerSize, string> = {
  xs: 'h-4 w-4 border-2',
  sm: 'h-5 w-5 border-2',
  md: 'h-8 w-8 border-2',
  lg: 'h-12 w-12 border-2',
};

const VARIANT_MAP: Record<LoadingSpinnerVariant, string> = {
  primary: 'border-bgPrimary border-t-transparent',
  default: 'border-primary border-t-transparent',
  muted: 'border-border border-t-transparent',
  white: 'border-white border-t-transparent',
};

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  variant = 'primary',
  className = '',
}) => {
  return (
    <div
      className={`animate-spin rounded-full ${SIZE_MAP[size]} ${VARIANT_MAP[variant]} ${className}`}
      role="status"
      aria-label="Laddar"
    />
  );
};

/**
 * LoadingState - Centrerad laddningsindikator med valfri höjd
 * Används för fullsidig/blockladdning
 */
export interface LoadingStateProps {
  /** Minsta höjd (t.ex. h-64, min-h-[400px]) */
  minHeight?: string;
  /** Ytterligare CSS-klasser */
  className?: string;
  /** Spinner-storlek */
  size?: LoadingSpinnerSize;
  /** Spinner-variant */
  variant?: LoadingSpinnerVariant;
  /** Visa anpassad spinner istället */
  children?: React.ReactNode;
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  minHeight = 'h-64',
  className = '',
  size = 'lg',
  variant = 'primary',
  children,
}) => {
  return (
    <div
      className={`flex justify-center items-center ${minHeight} ${className}`}
      role="status"
      aria-label="Laddar"
    >
      {children ?? <LoadingSpinner size={size} variant={variant} />}
    </div>
  );
};
